import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  Chip,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  BugReport as BugIcon,
  Speed as SpeedIcon,
  Lightbulb as LightbulbIcon,
  Image as ImageIcon,
  Send as SendIcon,
  VpnKey as AuthIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import {
  submitFeedback,
  getMyFeedback,
} from '../services/betaFeedbackService';
import {
  FeedbackCategory,
  CATEGORY_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  IBetaFeedback,
} from '../types/betaFeedback';
import { stitchColors, stitchFonts } from '../theme/tokens';

const MAX_FILES = 3;
const MAX_FILE_SIZE_MB = 5;

const CATEGORY_ICONS: Record<FeedbackCategory, React.ReactNode> = {
  improvement: <LightbulbIcon fontSize="small" />,
  bug: <BugIcon fontSize="small" />,
  performance: <SpeedIcon fontSize="small" />,
  authentication: <AuthIcon fontSize="small" />,
};

const CATEGORY_COLORS: Record<FeedbackCategory, string> = {
  improvement: stitchColors.surfaceTint,
  bug: stitchColors.error,
  performance: stitchColors.secondary,
  authentication: stitchColors.onTertiaryFixedVariant,
};

const BetaFeedback: React.FC = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['betaFeedback', 'mine'],
    queryFn: getMyFeedback,
    enabled: !!user,
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      submitFeedback(category as FeedbackCategory, description, attachments),
    onSuccess: () => {
      setSubmitSuccess(true);
      setCategory('');
      setDescription('');
      setAttachments([]);
      queryClient.invalidateQueries({ queryKey: ['betaFeedback', 'mine'] });
      setTimeout(() => setSubmitSuccess(false), 6000);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError('');
    const selected = Array.from(e.target.files || []);

    const invalid = selected.filter(
      (f) => !['image/png', 'image/jpeg'].includes(f.type)
    );
    if (invalid.length > 0) {
      setFileError('Only PNG and JPEG images are allowed.');
      e.target.value = '';
      return;
    }

    const oversized = selected.filter(
      (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024
    );
    if (oversized.length > 0) {
      setFileError(`Each file must be under ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }

    const combined = [...attachments, ...selected].slice(0, MAX_FILES);
    if (attachments.length + selected.length > MAX_FILES) {
      setFileError(`Maximum ${MAX_FILES} screenshots allowed.`);
    }
    setAttachments(combined);
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setFileError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !description.trim()) return;
    submitMutation.mutate();
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        pb: 4,
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${stitchColors.outlineVariant}`,
          pb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <FeedbackIcon sx={{ fontSize: 32, color: stitchColors.primary }} />
          <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '2rem', md: undefined } }}>
            Feedback
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '1.125rem', lineHeight: 1.6, color: stitchColors.onSurfaceVariant, maxWidth: 640 }}>
          Help us improve the Wills App by sharing your experience, reporting
          bugs, and flagging any issues. Your feedback is invaluable — thank you!
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Submission Form */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: `1px solid ${stitchColors.outlineVariant}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <SendIcon sx={{ color: stitchColors.primary }} />
              <Typography variant="h6" sx={{ fontFamily: stitchFonts.headline, fontWeight: 600 }}>
                Submit Feedback
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
              {submitSuccess && (
                <Alert
                  icon={<CheckCircleIcon />}
                  severity="success"
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  Feedback submitted successfully! Thank you for helping improve
                  the app.
                </Alert>
              )}

              {submitMutation.isError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {(submitMutation.error as any)?.response?.data?.error ||
                    'Failed to submit feedback. Please try again.'}
                </Alert>
              )}

              {/* Category Dropdown */}
              <FormControl fullWidth required sx={{ mb: 3 }}>
                <InputLabel>Feedback Type *</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
                  label="Feedback Type *"
                >
                  {(Object.entries(CATEGORY_LABELS) as [FeedbackCategory, string][]).map(
                    ([value, label]) => (
                      <MenuItem key={value} value={value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ color: CATEGORY_COLORS[value], display: 'flex' }}>
                            {CATEGORY_ICONS[value]}
                          </Box>
                          {label}
                        </Box>
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Category description hint */}
              {category && (
                <Alert
                  severity="info"
                  sx={{ mb: 3, borderRadius: 2, fontSize: '0.85rem' }}
                  icon={
                    <Box sx={{ color: CATEGORY_COLORS[category as FeedbackCategory], display: 'flex' }}>
                      {CATEGORY_ICONS[category as FeedbackCategory]}
                    </Box>
                  }
                >
                  {category === 'improvement' &&
                    'Describe your idea for improving the app — what problem it solves and how it could work.'}
                  {category === 'bug' &&
                    'Describe what happened, what you expected to happen, and the steps to reproduce it.'}
                  {category === 'performance' &&
                    'Describe where the app feels slow or unresponsive — which screen, action, or flow.'}
                  {category === 'authentication' &&
                    'Describe the login or access issue you experienced, including any error messages.'}
                </Alert>
              )}

              {/* Description */}
              <TextField
                label="Description *"
                multiline
                rows={6}
                fullWidth
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please provide as much detail as possible..."
                inputProps={{ maxLength: 5000 }}
                helperText={`${description.length} / 5000 characters`}
                sx={{ mb: 3 }}
              />

              {/* File Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Screenshots (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Attach up to {MAX_FILES} PNG or JPEG screenshots (max {MAX_FILE_SIZE_MB} MB each)
                </Typography>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachments.length >= MAX_FILES}
                  sx={{ mb: 1.5 }}
                >
                  {attachments.length >= MAX_FILES
                    ? 'Maximum files reached'
                    : 'Upload Screenshots'}
                </Button>

                {fileError && (
                  <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2, fontSize: '0.85rem' }}>
                    {fileError}
                  </Alert>
                )}

                {attachments.length > 0 && (
                  <Stack spacing={1}>
                    {attachments.map((file, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          bgcolor: stitchColors.surfaceContainerLow,
                          borderRadius: 1,
                          border: `1px solid ${stitchColors.outlineVariant}`,
                        }}
                      >
                        <ImageIcon sx={{ color: stitchColors.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => removeAttachment(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={!category || !description.trim() || submitMutation.isPending}
                startIcon={
                  submitMutation.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  boxShadow: '0 1px 2px rgba(18,28,40,0.08)',
                }}
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit Feedback'}
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Sidebar: my history + help */}
        <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>
          {/* Feedback Types Reference */}
          <Paper sx={{ mb: 3, overflow: 'hidden' }}>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: `1px solid ${stitchColors.outlineVariant}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontFamily: stitchFonts.headline, fontWeight: 600 }}>
                Feedback Types
              </Typography>
            </Box>
            <List dense sx={{ px: 1 }}>
              {(Object.entries(CATEGORY_LABELS) as [FeedbackCategory, string][]).map(
                ([value, label]) => (
                  <ListItem key={value}>
                    <ListItemIcon sx={{ minWidth: 36, color: CATEGORY_COLORS[value] }}>
                      {CATEGORY_ICONS[value]}
                    </ListItemIcon>
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                    />
                  </ListItem>
                )
              )}
            </List>
          </Paper>

          {/* My Feedback History */}
          <Paper sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: `1px solid ${stitchColors.outlineVariant}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontFamily: stitchFonts.headline, fontWeight: 600 }}>
                My Submissions
              </Typography>
              {historyData && (
                <Chip
                  label={historyData.total}
                  size="small"
                  color="primary"
                />
              )}
            </Box>

            <Box sx={{ p: 2 }}>
              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !historyData?.feedback?.length ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: 'center' }}
                >
                  No feedback submitted yet
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {historyData.feedback.map((item: IBetaFeedback) => (
                    <Accordion
                      key={item._id}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: `1px solid ${stitchColors.outlineVariant}`,
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="small" />}
                        sx={{ px: 2, py: 0.5, minHeight: 48 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              color: CATEGORY_COLORS[item.category],
                              display: 'flex',
                              flexShrink: 0,
                            }}
                          >
                            {CATEGORY_ICONS[item.category]}
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            noWrap
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            {CATEGORY_LABELS[item.category]}
                          </Typography>
                          <Chip
                            label={STATUS_LABELS[item.status]}
                            size="small"
                            color={STATUS_COLORS[item.status]}
                            sx={{ fontSize: '0.7rem', height: 20, flexShrink: 0 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 0.5 }}
                        >
                          {new Date(item.createdAt).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            maxHeight: 100,
                            overflow: 'auto',
                          }}
                        >
                          {item.description}
                        </Typography>
                        {item.attachments?.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              {item.attachments.length} screenshot
                              {item.attachments.length > 1 ? 's' : ''} attached
                            </Typography>
                          </Box>
                        )}
                        {item.adminNotes && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              bgcolor: 'success.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'success.200',
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="success.dark"
                              fontWeight={600}
                              display="block"
                            >
                              Team Response:
                            </Typography>
                            <Typography variant="caption" color="success.dark">
                              {item.adminNotes}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default BetaFeedback;
